import { Component } from '@theme/component';
import { debounce, onDocumentReady } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 250;
const DEACTIVATE_DELAY = 350;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  connectedCallback() {
    super.connectedCallback();

    onDocumentReady(this.#preloadImages);
    this.#initMegaAccordion();
    this.#initOverflowMenu();
    this.#highlightActiveLink();
    this.#disableOurAppsClick();
    // Pre-populate chevrons after overflow-list has assigned slots
    requestAnimationFrame(() => requestAnimationFrame(() => this.#setupAccordionChevrons()));
  }

  #disableOurAppsClick() {
    const menuLinks = this.querySelectorAll(".menu-list__link");
    menuLinks.forEach((link) => {
      const linkText = link.textContent?.trim();
      const linkUrl = link.getAttribute("data-link-url") || link.getAttribute("href");
      
      if (linkText === "Our Apps" || linkUrl?.includes("/our-app")) {
        link.style.cursor = "default";
        link.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
        });
      }
    });
  }

  #highlightActiveLink() {
    const currentPath = window.location.pathname;
    const menuLinks = this.querySelectorAll(".menu-list__link");

    menuLinks.forEach((link) => {
      const linkUrl =
        link.getAttribute("data-link-url") || link.getAttribute("href");
      if (!linkUrl) return;

      // Skip highlighting for "Our Apps" menu item
      const linkText = link.textContent?.trim();
      if (
        linkText === "Our Apps" ||
        linkUrl.includes("/our-app") ||
        linkUrl.includes("#")
      ) {
        return;
      }

      // Remove any query strings or hashes for comparison
      const cleanLinkUrl = linkUrl.split("?")[0].split("#")[0];
      const cleanCurrentPath = currentPath.split("?")[0].split("#")[0];

      // Check if this link matches the current page
      if (
        cleanLinkUrl === cleanCurrentPath ||
        (cleanCurrentPath.includes(cleanLinkUrl) && cleanLinkUrl !== "/")
      ) {
        link.classList.add("menu-list__link--active");
      }
    });
  }

  #initMegaAccordion() {
    // Event delegation inside header-menu only
    this.addEventListener('click', (event) => {
      const trigger = event.target.closest('.mega-accordion__trigger');
      if (!trigger) return;

      const accordion = trigger.closest('.mega-accordion');
      if (!accordion) return;

      const content = accordion.querySelector('.mega-accordion__content');
      if (!content) return;

      event.preventDefault();
      event.stopPropagation();

      const isOpen = accordion.classList.contains('is-open');

      this.querySelectorAll('.mega-accordion.is-open').forEach((openAccordion) => {
        if (openAccordion !== accordion) {
          openAccordion.classList.remove('is-open');
          const openTrigger = openAccordion.querySelector('.mega-accordion__trigger');
          openTrigger?.setAttribute('aria-expanded', 'false');
        }
      });

      accordion.classList.toggle('is-open');
      trigger.setAttribute('aria-expanded', String(!isOpen));
    });
  }

  #initOverflowMenu() {
    this._overflowClickOpened = false;
    this._activeOverflowLink = null;

    this.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;

      // --- MORE button toggle ---
      const moreTrigger = event.target.closest('[slot="more"]');
      if (moreTrigger) {
        event.preventDefault();
        event.stopPropagation();
        const isExpanded = this.dataset.overflowExpanded === 'true';
        if (isExpanded) {
          this.dataset.overflowExpanded = 'false';
          this._overflowClickOpened = false;
          this.#clearOverflowSubmenuPanel({ preserveContent: true });
          this.#deactivate();
        } else {
          this.dataset.overflowExpanded = 'true';
          this._overflowClickOpened = true;
          // Defer so overflow-list finishes setting slot="overflow" before we query
          requestAnimationFrame(() => {
            this.#setupAccordionChevrons();
            this.#preSetOverflowCardHeight();
          });
        }
        return;
      }

      // Only process inner clicks when dropdown is open
      if (this.dataset.overflowExpanded !== 'true') return;

      // --- Overflow item click (flyout mode) ---
      const overflowLink = event.target.closest('[slot="overflow"] [data-menu="menuitem"]');
      if (overflowLink) {
        const itemId = overflowLink.dataset.id;
        const submenuEl = document.querySelector(`[ref="submenu[]"][data-id="${itemId}"]`);
        const hasSubmenu = !!(submenuEl?.querySelector('.megamenu-wrapper') ?? submenuEl?.querySelector('.mega-menu__link'));

        if (hasSubmenu) {
          // Has submenu — show right panel, don't navigate
          event.preventDefault();
          event.stopPropagation();
          this.#debouncedActivateHandler?.cancel?.();
          this.#setupAccordionChevrons();
          this.querySelectorAll('[slot="overflow"] [data-menu="menuitem"][data-overflow-active]').forEach(
            (link) => link.removeAttribute('data-overflow-active')
          );
          overflowLink.setAttribute('data-overflow-active', 'true');
          this._activeOverflowLink = overflowLink;
          this.#renderOverflowSubmenu(overflowLink);
        } else {
          // No submenu — close card, navigate naturally
          this.dataset.overflowExpanded = 'false';
          this._overflowClickOpened = false;
          this.#clearOverflowSubmenuPanel();
          this.#deactivate();
        }
        return;
      }
    });

    // --- Close on outside click ---
    document.addEventListener('click', (event) => {
      if (this.dataset.overflowExpanded !== 'true') return;
      const path = event.composedPath();
      if (path.includes(this)) return; // Click was inside header-menu
      this.dataset.overflowExpanded = 'false';
      this._overflowClickOpened = false;
      this.#clearOverflowSubmenuPanel({ preserveContent: true });
      this.#deactivate();
    });

    // --- Fallback: deactivate when pointer exits the ENTIRE overflow panel ---
    // Covers the case where pointer leaves via shadow-DOM area without
    // passing through any <li> pointerleave.
    const overflowList = this.querySelector('.overflow-menu');
    if (overflowList) {
      overflowList.addEventListener('pointerleave', (event) => {
        if (this.dataset.overflowExpanded !== 'true') return;
        if (this._overflowClickOpened) return;
        this.#debouncedDeactivate(event);
      });
    }
  }

  #clearOverflowSubmenuPanel({ preserveContent = false } = {}) {
    const host = this.querySelector('[slot="overflow-submenu"]');
    if (host instanceof HTMLElement && !preserveContent) host.innerHTML = '';
    this.dataset.overflowSubmenu = 'false';
    this.style.removeProperty('--overflow-submenu-width');
    this.style.removeProperty('--overflow-submenu-height');
    this._activeOverflowLink = null;
    this.querySelectorAll('[slot="overflow"] [data-menu="menuitem"][data-overflow-active]').forEach(
      (link) => link.removeAttribute('data-overflow-active')
    );
  }

  /** Mark overflow items that have sub-menus and inject chevron spans */
  #setupAccordionChevrons() {
    const overflowLinks = this.querySelectorAll('[slot="overflow"] [data-menu="menuitem"]');
    overflowLinks.forEach((link) => {
      if (link.classList.contains('has-overflow-submenu')) return; // Already done
      const itemId = link.dataset.id;
      const hasSubmenu = !!document.querySelector(`[ref="submenu[]"][data-id="${itemId}"]`);
      if (hasSubmenu) {
        link.classList.add('has-overflow-submenu');
        const chevron = document.createElement('span');
        chevron.className = 'overflow-chevron';
        chevron.setAttribute('aria-hidden', 'true');
        link.appendChild(chevron);
      }
    });
  }

  /** Pre-calculate max submenu height across all overflow items and set the CSS variable.
   *  This prevents the card from jumping in height when the user first clicks an item.
   */
  #preSetOverflowCardHeight() {
    const overflowLinks = this.querySelectorAll('[slot="overflow"] [data-menu="menuitem"]');
    let maxH = 0;

    overflowLinks.forEach((link) => {
      const submenu = document.querySelector(`[ref="submenu[]"][data-id="${link.dataset.id}"]`);
      const wrapper = submenu?.querySelector('.megamenu-wrapper');
      if (!(wrapper instanceof HTMLElement)) return;

      // Clone off-screen inside a .overflow-inline-submenu wrapper so all
      // parent-selector rules (e.g. .overflow-inline-submenu .menu-heading)
      // apply correctly during measurement.
      const tmp = /** @type {HTMLElement} */ (wrapper.cloneNode(true));
      tmp.classList.add('overflow-inline-submenu__content');
      tmp.style.height = 'auto'; // prevent height:100% from collapsing it
      const measureWrap = document.createElement('div');
      measureWrap.className = 'overflow-inline-submenu';
      measureWrap.style.cssText = 'position:fixed;visibility:hidden;height:auto;top:-9999px;left:-9999px;width:420px;';
      measureWrap.appendChild(tmp);
      document.body.appendChild(measureWrap);
      const h = tmp.scrollHeight;
      document.body.removeChild(measureWrap);
      if (h > maxH) maxH = h;
    });

    if (maxH > 0) {
      this.style.setProperty('--overflow-submenu-height', `${maxH}px`);
    }
  }

  #renderOverflowSubmenu(item) {
    const overflowSubmenuHost = this.querySelector('[slot="overflow-submenu"]');
    if (!(overflowSubmenuHost instanceof HTMLElement)) return null;

    const submenu = document.querySelector(`[ref="submenu[]"][data-id="${item.dataset.id}"]`);
    const submenuContent = submenu?.querySelector('.megamenu-wrapper');

    if (!(submenuContent instanceof HTMLElement)) {
      overflowSubmenuHost.innerHTML = '';
      this.dataset.overflowSubmenu = 'false';
      this.style.removeProperty('--overflow-submenu-width');
      this.style.removeProperty('--overflow-submenu-height');
      return null;
    }

    const clone = submenuContent.cloneNode(true);
    if (!(clone instanceof HTMLElement)) return null;

    clone.classList.add('overflow-inline-submenu__content');
    this.#initializeOverflowSubmenu(clone);
    overflowSubmenuHost.replaceChildren(clone);
    this.dataset.overflowSubmenu = 'true';
    this.style.setProperty('--overflow-submenu-width', `${clone.offsetWidth}px`);

    // Temporarily break the height:100% chain so scrollHeight returns the
    // content's natural height, not the CSS Grid stretched height.
    // Both the host and the clone carry height:100% from the stylesheet —
    // overriding both here prevents the circular dependency.
    overflowSubmenuHost.style.height = 'auto';
    clone.style.height = 'auto';
    const naturalHeight = clone.scrollHeight; // true content height (forces sync reflow)
    clone.style.removeProperty('height');
    overflowSubmenuHost.style.removeProperty('height');

    // Keep the max so the card never shrinks when the user switches items
    const prevH = parseInt(this.style.getPropertyValue('--overflow-submenu-height') || '0', 10);
    if (naturalHeight > prevH) this.style.setProperty('--overflow-submenu-height', `${naturalHeight}px`);
    return clone;
  }

  #initializeOverflowSubmenu(clone) {
    const wrappers = clone.querySelectorAll('.column-menu-wraper');

    wrappers.forEach((wrapper) => {
      const contentContainer = wrapper.querySelector('.content-show-here');
      const menuItems = wrapper.querySelectorAll('.mega-menu__item.has-submenu');

      if (!(contentContainer instanceof HTMLElement) || menuItems.length === 0) return;

      const showSubmenu = (menuItem) => {
        if (!(menuItem instanceof HTMLElement)) return;

        const submenu = menuItem.querySelector('.mega-submenu-content');
        if (!(submenu instanceof HTMLElement)) return;

        menuItems.forEach((item) => {
          item.querySelector('.mega-menu__link')?.classList.remove('mega-menu__link-active');
        });

        menuItem.querySelector('.mega-menu__link')?.classList.add('mega-menu__link-active');
        contentContainer.innerHTML = '';

        const submenuClone = submenu.cloneNode(true);
        if (!(submenuClone instanceof HTMLElement)) return;

        submenuClone.style.display = 'block';
        submenuClone.classList.add('overflow-inline-submenu-panel');
        contentContainer.appendChild(submenuClone);
      };

      const firstItem = menuItems[0];
      if (firstItem instanceof HTMLElement) {
        showSubmenu(firstItem);
      }

      menuItems.forEach((item) => {
        item.addEventListener('pointerenter', () => showSubmenu(item));
        item.addEventListener('focusin', () => showSubmenu(item));
      });
    });
  }


  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();

    if (this.#state.activeItem) {
      this.#activateHandler(event);
    } else {
      this.#debouncedActivateHandler(event);
    }
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    const moreTrigger = event.target.closest('[slot="more"]');
    if (moreTrigger) {
      this.dataset.overflowExpanded = 'true';
      return;
    }

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isOverflowItem = item.closest('[slot="overflow"]') !== null;

    // Don't stomp click-opened overflow state when hovering regular items
    if (!this._overflowClickOpened) {
      this.dataset.overflowExpanded = isOverflowItem.toString();
    }

    this.#deactivate();

    if (isOverflowItem) {
      // Flyout mode: hovering shows the right-panel submenu.
      this.#setupAccordionChevrons();
      this.querySelectorAll('[slot="overflow"] [data-menu="menuitem"][data-overflow-active]').forEach(
        (link) => link.removeAttribute('data-overflow-active')
      );
      item.setAttribute('data-overflow-active', 'true');
      this._activeOverflowLink = item;
      this.#renderOverflowSubmenu(item);
      return;
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    const anchorElement = item;
    const itemRect = anchorElement?.getBoundingClientRect();
    const x = itemRect ? itemRect.left + itemRect.width / 2 : undefined;
    const y = 0;

    // ✅ Pass x to findSubmenu
    const submenu = findSubmenu(item, x, y, isOverflowItem);

    if (!submenu) return;
    const submenuHeight = submenu.offsetHeight + y;
    this.dataset.overflowSubmenu = 'false';
    this.style.removeProperty('--overflow-submenu-width');
    this.style.removeProperty('--overflow-submenu-height');
    submenu?.parentElement?.style.setProperty('--submenu-height', `${submenuHeight}px`);
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    // Click-opened mode: pointer events must never close the dropdown
    if (this._overflowClickOpened) return;

    // When overflow panel is open in hover mode, check relatedTarget.
    // Moving from a <li> into the shadow-DOM right column retargets
    // relatedTarget to the overflow-list host — don't deactivate in that case.
    if (this.dataset.overflowExpanded === 'true') {
      const overflowList = this.querySelector('.overflow-menu');
      const relatedTarget = event instanceof PointerEvent ? event.relatedTarget : null;
      if (
        overflowList &&
        relatedTarget instanceof Node &&
        (relatedTarget === overflowList || overflowList.contains(relatedTarget))
      ) {
        return; // Pointer still inside overflow panel — keep it open
      }
    }

    if (!(event.target instanceof Element)) {
      this.#debouncedDeactivate(event);
      return;
    }

    const item = findMenuItem(event.target);

    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate(event);
    } else if (!item && this.#state.activeItem) {
      this.#debouncedDeactivate(event);
    }
  }


  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce((event) => {
    // Don't close click-opened overflow dropdown via pointer events
    if (!this._overflowClickOpened) {
      this.dataset.overflowExpanded = 'false';
      this.#clearOverflowSubmenuPanel({ preserveContent: true });
    }

    const item = findMenuItem(event?.target);

    if (!item && this.#state.activeItem) {
      this.#deactivate();
      return;
    }

    if (item != this.#state.activeItem) return;

    this.#deactivate(item);
  }, DEACTIVATE_DELAY);


  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    const submenu = findSubmenu(item);
    submenu?.parentElement?.style.setProperty('--submenu-height', '0px');

    // Only clear overflow submenu panel when NOT in click-opened mode
    if (!this._overflowClickOpened) {
      this.#clearOverflowSubmenuPanel({ preserveContent: true });
    }

    if (!item) return;

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
    }, this.animationDelay);
  };

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  const moreItem = element.closest('[slot="more"]');
  if (moreItem) {
    return findMenuItem(
      moreItem.parentElement?.querySelector('[slot="overflow"] [data-menu="menuitem"]')
    );
  }

  const menuItem = element.closest('[data-menu="menuitem"]') || element.querySelector('[data-menu="menuitem"]');
  return menuItem instanceof HTMLElement ? menuItem : null;
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element, x, y = 0, isOverflowItem = false) {
  const clickedId = element?.dataset?.id;
  const menuBar = document.querySelectorAll('[ref="submenu[]"]');

  // Hide all submenus
  menuBar?.forEach(megamenu => {
    megamenu.classList.add('hidden');
    megamenu.classList.remove('mega-menu-block--overflow-aligned');
    megamenu.style.removeProperty('--anchor-y');
    megamenu.parentElement?.classList.remove('mega-menu-group--overflow-open');
  });

  let matchedSubmenu = null;

  // Show the matched submenu
  menuBar?.forEach(megamenu => {
    const submenuId = megamenu.getAttribute('data-id');
    if (clickedId === submenuId) {
      megamenu.classList.remove('hidden');
      matchedSubmenu = megamenu;
      // ✅ Now x is defined — use it
      if (x !== undefined) {
        matchedSubmenu.style.setProperty('--anchor-x', `${x}px`);
      }
      matchedSubmenu.style.setProperty('--anchor-y', `${y}px`);
      matchedSubmenu.classList.toggle('mega-menu-block--overflow-aligned', isOverflowItem);
      matchedSubmenu.parentElement?.classList.toggle('mega-menu-group--overflow-open', isOverflowItem);
    }
  });

  return matchedSubmenu instanceof HTMLElement ? matchedSubmenu : null;
}




document.querySelectorAll('.menu-drawer__menu-item').forEach(item => {
  item.addEventListener('click', function (e) {
    const submenu2 = findSubmenu(item);
    console.log('Submenu found:', submenu2);
  });
});


document.addEventListener('DOMContentLoaded', function () {
  const detailsList = document.querySelectorAll('accordion-custom details');

  detailsList.forEach((details) => {
    details.addEventListener('toggle', function () {
      if (details.open) {
        // Close all other open details
        detailsList.forEach((other) => {
          if (other !== details) {
            other.removeAttribute('open');
          }
        });

        // Scroll .details-content to top reliably
        const content = details.querySelector('.details-content');
        if (content) {
          // Ensure DOM has updated before scrolling
          requestAnimationFrame(() => {
            setTimeout(() => {
              content.scrollTop = 0;
            }, 50); // Small delay helps in real devices
          });
        }
      }
    });
  });
});

  
